<template>
    <div style="width: 100%;">
        <!-- Mobile menu button -->
        <div class="mobile-menu-header">
            <button class="burger-button" @click="toggleMobileMenu">
                <svg class="burger-icon" :class="{ 'burger-icon--open': mobileMenuOpen }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path v-if="!mobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
                    <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
                <span>Меню</span>
            </button>
        </div>

        <!-- Mobile menu container -->
        <div class="mobile-menu-container" :class="{ 'mobile-menu-container--open': mobileMenuOpen }">
            <menu-section :items="sections" :hash="hash" @loadItems="handleLoadItems" @itemClick="handleItemClick"></menu-section>
        </div>

        <v-row align="start">
            <v-col cols="2" class="d-none d-md-block desktop-menu">
                <div class="menu-wrapper">
                    <menu-section :items="sections" :hash="hash" @loadItems="handleLoadItems" @itemClick="handleItemClick"></menu-section>
                </div>
            </v-col>
            <v-col cols="12" md="10">
                <div class="content-wrapper-inner">
                    <div v-if="!initialLoading" class="public-section-search">
                        <v-text-field
                            v-model="searchQuery"
                            class="public-section-search__input"
                            dense
                            outlined
                            hide-details
                            clearable
                            placeholder="Поиск по текущему разделу"
                            append-icon="mdi-magnify"
                            @keydown.enter.prevent="runSearch"
                            @click:clear="clearSearch"
                        />
                        <v-btn
                            class="public-section-search__button"
                            :loading="searchLoading"
                            :disabled="searchLoading"
                            @click="runSearch"
                        >
                            Найти
                        </v-btn>
                    </div>
                    <div v-if="initialLoading" class="initial-loading">
                        <div class="loading-spinner">
                            <svg class="spinner" viewBox="0 0 50 50">
                                <circle class="spinner-circle" cx="25" cy="25" r="20" fill="none" stroke-width="4"></circle>
                            </svg>
                            <span class="loading-text">Загрузка...</span>
                        </div>
                    </div>
                    <div v-else-if="searchActive">
                        <h1 class="public-section-search__title">
                            {{ searchType === 'tag' ? `Статьи с тегом «${activeQuery}»` : `Результаты поиска: ${activeQuery}` }}
                        </h1>
                        <v-chip
                            v-if="searchType === 'tag'"
                            class="public-section-search__tag-chip"
                            close
                            small
                            @click:close="clearSearch"
                        >Тег: {{ activeQuery }}</v-chip>
                        <div v-if="searchItems.length === 0" class="public-section-search__empty">
                            Статьи не найдены.
                        </div>
                        <template v-else>
                            <v-card v-for="item in searchItems" :key="item.id" class="public-section-search__card">
                                <v-card-title>
                                    <a href="#" @click.prevent="openSearchItem(item.id)">{{ item.name }}</a>
                                </v-card-title>
                                <v-card-text v-html="item.description">
                                    {{ item.description }}
                                </v-card-text>
                                <v-card-text v-if="item.tags && item.tags.length" class="pt-0">
                                    <public-tag-list :tags="item.tags" @select="searchByTag"></public-tag-list>
                                </v-card-text>
                            </v-card>
                        </template>
                    </div>
                    <router-view v-else :key="$route.fullPath"></router-view>
                </div>
            </v-col>
        </v-row>
        <!-- Overlay for mobile -->
        <div class="menu-overlay" :class="{ 'menu-overlay--active': mobileMenuOpen }" @click="closeMobileMenu"></div>
    </div>
</template>

<script>
    import Vue from 'vue';
    import store from "../../public/store/store";
    import MenuSection from "./MenuSection.vue";
    import PublicTagList from "./PublicTagList.vue";

    export default {
        name: "PublicSection",
        components: {MenuSection, PublicTagList},
        props: ['hash', 'sectionId'],
        data() {
            return {
                sections: [],
                mobileMenuOpen: false,
                loadedSections: {}, // Map sectionId -> items
                initialLoading: true,
                searchQuery: '',
                searchLoading: false,
                searchActive: false,
                searchItems: [],
                searchType: 'text',
                activeQuery: '',
            }
        },
        methods: {
            toggleMobileMenu() {
                this.mobileMenuOpen = !this.mobileMenuOpen;
            },
            closeMobileMenu() {
                this.mobileMenuOpen = false;
            },
            handleLoadItems(data) {
                const { sectionId, hash, component } = data;

                // Return cached items if already loaded
                if (this.loadedSections[sectionId]) {
                    component.setItems(this.loadedSections[sectionId]);
                    return;
                }

                axios.post('/rest/share/section/items', {
                    sectionId: sectionId,
                    hash: hash
                }).then((response) => {
                    const items = response.data.items || [];
                    Vue.set(this.loadedSections, sectionId, items);
                    component.setItems(items);
                }).catch((error) => {
                    console.error('Error loading items:', error);
                    Vue.set(this.loadedSections, sectionId, []);
                    component.setItems([]);
                });
            },
            handleItemClick(item) {
                this.closeMobileMenu();
                this.searchActive = false;
            },
            runSearch() {
                const query = this.searchQuery.trim();

                if (query === '') {
                    this.clearSearch();
                    return;
                }

                this.searchQuery = query;
                this.performSearch(query, 'text');
            },
            searchByTag(tag) {
                this.searchQuery = '';
                this.performSearch(tag, 'tag');
            },
            performSearch(query, type) {
                this.searchLoading = true;

                axios.post('/rest/share/section/search', {
                    hash: this.hash,
                    sectionId: this.getCurrentSectionId(),
                    query,
                    type,
                }).then((response) => {
                    this.searchItems = response.data.items || [];
                    this.activeQuery = query;
                    this.searchType = type;
                    this.searchActive = true;
                    this.closeMobileMenu();
                }).catch((error) => {
                    console.error('Error searching public section:', error);
                    this.searchItems = [];
                    this.activeQuery = query;
                    this.searchType = type;
                    this.searchActive = true;
                }).finally(() => {
                    this.searchLoading = false;
                });
            },
            clearSearch() {
                this.searchQuery = '';
                this.activeQuery = '';
                this.searchType = 'text';
                this.searchItems = [];
                this.searchActive = false;
            },
            openSearchItem(itemId) {
                this.searchActive = false;
                this.$router.push({
                    name: 'section-detail-item',
                    params: { itemId },
                    query: { hash: this.hash }
                });
            },
            getCurrentSectionId() {
                const routeSectionId = Number(this.$route.params.sectionId || 0);

                if (routeSectionId > 0) {
                    return routeSectionId;
                }

                const propSectionId = Number(this.sectionId || 0);

                if (propSectionId > 0) {
                    return propSectionId;
                }

                return 0;
            },
            preloadSections(sections, hash) {
                // Preload items for all sections to expand them
                const preloadAll = async (sectionsList) => {
                    for (const section of sectionsList) {
                        // Skip if already loaded
                        if (this.loadedSections[section.id]) {
                            continue;
                        }

                        // Load items for this section
                        try {
                            const response = await axios.post('/rest/share/section/items', {
                                sectionId: section.id,
                                hash: hash
                            });
                            const items = response.data.items || [];
                            Vue.set(this.loadedSections, section.id, items);
                        } catch (error) {
                            console.error('Error preloading items for section:', section.id, error);
                            Vue.set(this.loadedSections, section.id, []);
                        }

                        // Recursively preload children
                        if (section.children && section.children.length > 0) {
                            await preloadAll(section.children);
                        }
                    }
                };

                return preloadAll(sections);
            },
            findFirstItem(sections) {
                // Recursively find the first item in the menu tree
                for (const section of sections) {
                    // If section has children, search in children first
                    if (section.children && section.children.length > 0) {
                        const foundInChild = this.findFirstItem(section.children);
                        if (foundInChild) {
                            return foundInChild;
                        }
                    }
                    // If section has items, return the first one
                    if (section.items && section.items.length > 0) {
                        return section.items[0];
                    }
                }
                return null;
            },
            loadFirstItem(sections, hash) {
                // First, try to find item by making requests to sections
                const loadItemsRecursively = (sectionsList, parentSection = null) => {
                    return new Promise((resolve, reject) => {
                        const loadNext = async (index) => {
                            if (index >= sectionsList.length) {
                                resolve(null);
                                return;
                            }

                            const section = sectionsList[index];

                            // If section has children, search in children first
                            if (section.children && section.children.length > 0) {
                                const foundInChild = await loadItemsRecursively(section.children, section);
                                if (foundInChild) {
                                    resolve(foundInChild);
                                    return;
                                }
                            }

                            // Try to load items for this section
                            try {
                                const response = await axios.post('/rest/share/section/items', {
                                    sectionId: section.id,
                                    hash: hash
                                });
                                const items = response.data.items || [];
                                if (items.length > 0) {
                                    // Cache the items
                                    Vue.set(this.loadedSections, section.id, items);
                                    resolve({ item: items[0], parentSection: section });
                                    return;
                                }
                            } catch (error) {
                                console.error('Error loading items for section:', section.id, error);
                            }

                            // Continue with next section
                            loadNext(index + 1);
                        };
                        loadNext(0);
                    });
                };

                return loadItemsRecursively(sections);
            }
        },
        beforeDestroy() {
            this.$root.$off('public-tag-search', this.searchByTag);
        },
        created() {
            store.commit('setCurrentHash', this.hash);
            this.$root.$on('public-tag-search', this.searchByTag);

            axios.post(`/rest/share/section/${this.hash}`).then((response) => {
                this.sections = response.data.sections;

                // Load first item if no current route
                if (!this.$route.params.itemId) {
                    this.loadFirstItem(this.sections, this.hash).then((result) => {
                        if (result && result.item) {
                            // Preload all sections before redirecting
                            this.preloadSections(this.sections, this.hash).then(() => {
                                // Wait for child components to be ready
                                setTimeout(() => {
                                    this.initialLoading = false;
                                    this.$router.replace({
                                        name: 'section-detail-item',
                                        params: { itemId: result.item.id },
                                        query: { hash: this.hash }
                                    });
                                }, 100);
                            });
                        }
                    });
                } else {
                    // If we already have an itemId, don't show initial loading
                    // SectionItem.vue will handle its own loading state
                    this.initialLoading = false;

                    // Preload items for all sections to expand menu
                    this.preloadSections(this.sections, this.hash);
                }
            });
        }
    }
</script>

<style scoped>
.mobile-menu-header {
    display: none;
    padding: 10px 16px;
    background: #ffffff;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 16px;
    position: sticky;
    top: 0;
    z-index: 100;
}

.burger-button {
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    color: #374151;
    font-size: 16px;
    font-weight: 500;
    width: 100%;
    justify-content: flex-start;
}

.burger-button:hover {
    background-color: #f3f4f6;
    border-radius: 8px;
}

.burger-icon {
    width: 24px;
    height: 24px;
    transition: transform 0.3s;
}

.mobile-menu-container {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: 300px;
    max-width: 85vw;
    z-index: 1000;
    background: #ffffff;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease-in-out;
    transform: translateX(-100%);
    overflow: hidden;
}

.mobile-menu-container--open {
    transform: translateX(0);
}

.menu-overlay {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
    opacity: 0;
    transition: opacity 0.3s;
    pointer-events: none;
}

.menu-overlay--active {
    opacity: 1;
    pointer-events: auto;
}

.desktop-menu {
    padding: 0 !important;
}

.menu-wrapper {
    margin-top: 60px;
}

.content-wrapper-inner {
    width: 100%;
    padding: 0 16px;
    box-sizing: border-box;
    margin-top: 30px;
}

.public-section-search {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
}

.public-section-search__input {
    flex: 1 1 auto;
}

.public-section-search__button {
    background-color: #1976d2 !important;
    color: #ffffff !important;
}

.public-section-search__title {
    color: #1f2937;
    font-size: 28px;
    font-weight: 600;
    line-height: 1.3;
    margin: 0 0 25px;
}

.public-section-search__empty {
    align-items: center;
    display: flex;
    justify-content: center;
    min-height: 240px;
    padding: 24px;
}

.public-section-search__card {
    margin-bottom: 30px;
}

.public-section-search__tag-chip {
    margin-bottom: 16px;
}

.public-section-search__card :deep(.v-card__text) {
    overflow-wrap: anywhere;
    white-space: normal;
}

/* Fix Vuetify col alignment */
.v-col.cols-2,
.v-col.cols-12 {
    align-self: flex-start !important;
}

/* Ensure v-row aligns items to top */
.v-row {
    align-items: flex-start !important;
}

/* Global fix for Vuetify row alignment - apply to all rows in this component */
:deep(.v-row) {
    align-items: flex-start !important;
}

:deep(.v-col) {
    align-self: flex-start !important;
}

.initial-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    padding: 40px;
}

.loading-spinner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
}

.spinner {
    width: 50px;
    height: 50px;
    animation: spin 1s linear infinite;
}

.spinner-circle {
    stroke: #3b82f6;
    stroke-linecap: round;
    stroke-dasharray: 90, 120;
}

.loading-text {
    font-size: 16px;
    color: #6b7280;
    font-weight: 500;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 960px) {
    .mobile-menu-header {
        display: block;
    }

    .mobile-menu-container {
        display: block;
    }

    .menu-overlay {
        display: block;
    }

    .desktop-menu {
        display: none !important;
    }

    .content-wrapper-inner {
        padding: 0 16px;
    }
}

@media (max-width: 530px) {
    .public-section-search {
        align-items: stretch;
        flex-direction: column;
    }
}
</style>
